import React, { MutableRefObject, useEffect, useRef, useState } from "react"
import classNames from "classnames"

import { ReactComponent as IconPhoto } from "assets/svg/icon-photo.svg"

import { getFileUrl } from "utils/getFileUrl"
import { logoErrorMessage, validateLogoFile } from "utils/validateLogoFile"

import styles from "./index.module.scss"

interface Props {
  onSetFile: (value: FileList | null) => void
  onValidationError?: (errorMessage: string) => void
  imageUrl?: string
  id?: string
  value?: File[] | null
  name?: string
  label?: string
  className?: string
  imageWrapperClassName?: string
  fileWrapperClassName?: string
  defaultLink?: string
  errorMessage?: string
  formatNote?: string
}

export const InputLogo = ({
  label,
  className,
  imageWrapperClassName,
  fileWrapperClassName,
  id,
  errorMessage,
  value,
  name,
  defaultLink,
  imageUrl,
  formatNote,
  onSetFile,
  onValidationError,
}: Props) => {
  const [isDragging, setIsDragging] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[] | null>(value ? value : null)
  const uploadAvatarRef: MutableRefObject<HTMLInputElement | null> = useRef(null)

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const files = e.dataTransfer.files
    updateLogo(files)
  }

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    updateLogo(files)
  }

  const updateLogo = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      onSetFile(null)
      setUploadedFiles(null)
      return
    }

    const file = files[0]

    try {
      const validationResult = await validateLogoFile(file)

      if (!validationResult.isValid) {
        if (onValidationError && validationResult.errorMessage) {
          onValidationError(validationResult.errorMessage)
        }
        // Clear the file input
        if (uploadAvatarRef.current) {
          uploadAvatarRef.current.value = ""
        }
        setUploadedFiles(null)
        return
      }

      if (validationResult.resizedFile) {
        onSetFile(null)
        setUploadedFiles([validationResult.resizedFile])
        const dt = new DataTransfer()
        dt.items.add(validationResult.resizedFile)
        const customFileList = dt.files
        onSetFile(customFileList)
      } else {
        onSetFile(files)
        setUploadedFiles([...files])
      }
    } catch (error) {
      console.error("Error validating logo file:", error)
      if (onValidationError) {
        onValidationError(logoErrorMessage)
      }
      if (uploadAvatarRef.current) {
        uploadAvatarRef.current.value = ""
      }
      setUploadedFiles(null)
    }
  }

  useEffect(() => {
    setUploadedFiles(value || null)
  }, [value])

  return (
    <div className={classNames(styles.fieldWrapper, className)}>
      <div className={styles.fieldLabelNew}>{label}</div>
      <div className={classNames(styles.field, styles.logoField)}>
        <div className={classNames(styles.imageWrapper, imageWrapperClassName)}>
          {uploadedFiles?.length ? (
            <img
              src={uploadedFiles ? getFileUrl(uploadedFiles[0]) : defaultLink}
              alt="file"
              className={styles.uploadedImage}
            />
          ) : imageUrl ? (
            <img src={imageUrl} alt="file" className={styles.uploadedImage} />
          ) : (
            <IconPhoto className={styles.icon} />
          )}
        </div>
        <input
          type="file"
          id={id}
          name={name}
          accept="image/png"
          className={styles.inputFile}
          onChange={onInputChange}
          ref={uploadAvatarRef}
        />
        <label
          htmlFor={id}
          className={classNames(
            styles.fileWrapper,
            {
              [styles.dragging]: isDragging,
              [styles.error]: errorMessage,
            },
            fileWrapperClassName,
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={styles.labelText}>
            <span>Click to upload</span> or drag and drop <br />
            <br />
            {formatNote}
          </div>
        </label>
      </div>
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
    </div>
  )
}
